import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { tap } from 'rxjs';
import { MatExpansionModule } from '@angular/material/expansion';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { NumberInputComponent, TextInputComponent } from '@examdojo/core/input';
import { SingleSelectComponent } from '@examdojo/core/select';
import {
  CanvasEditorComponent,
  CoordinateSystemEditorComponent,
  ControlFieldsEditorComponent,
  ReactionListEditorComponent,
  WIDGET_CONTROL_TYPE,
} from '@examdojo/core/widget';
import type {
  CanvasConfig,
  CoordinateSystemConfig,
  ReactionGroup,
  WidgetControl,
  WidgetSliderControl,
  WidgetButtonControl,
  WidgetDataTableControl,
} from '@examdojo/core/widget';
import type {
  ElementDef,
  ElementDisplayConfig,
  FeatureElementDef,
  FeatureType,
  FunctionElementDef,
  FunctionType,
  GraphpadDisplayConfig,
  GraphpadParams,
  PrimitiveElementDef,
  PrimitiveType,
  ValueExpression,
} from '../../graphpad.model';
import { isFeatureElementDef, isFunctionElementDef, isPrimitiveElementDef } from '../../graphpad.model';
import {
  getFeatureDefaultParams,
  getFeatureTypes,
  getFunctionMeta,
  getFunctionTypes,
  getPrimitiveDefaultParams,
  getPrimitiveTypes,
} from '../graphpad-editor.model';
import { ElementEditorComponent } from './element-editor.component';

interface SelectOption {
  label: string;
  value: string;
}

type EntryCategory = 'function' | 'feature' | 'primitive' | 'control';

interface FlatEntry {
  trackId: string;
  id: string;
  displayName: string;
  badge: string;
  category: EntryCategory;
  def?: ElementDef;
  display?: ElementDisplayConfig;
  isFunction?: boolean;
  isFeature?: boolean;
  isPrimitive?: boolean;
  control?: WidgetControl;
  controlIndex?: number;
}

@Component({
  selector: 'dojo-element-list-section',
  standalone: true,
  imports: [
    MatExpansionModule,
    ReactiveFormsModule,
    ButtonComponent,
    IconComponent,
    NumberInputComponent,
    TextInputComponent,
    SingleSelectComponent,
    ElementEditorComponent,
    CanvasEditorComponent,
    CoordinateSystemEditorComponent,
    ControlFieldsEditorComponent,
    ReactionListEditorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './element-list-section.component.html',
})
export class ElementListSectionComponent {
  constructor() {
    effect(() => {
      this.display();
      this.syncPrecisionFromConfig();
    });

    this.addCategoryCtrl.valueChanges
      .pipe(
        tap((val) => this.addCategory.set(val)),
        takeUntilDestroyed(),
      )
      .subscribe();
    this.newEntryTypeCtrl.valueChanges
      .pipe(
        tap((val) => this.selectedAddType.set(val)),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.defaultPrecisionCtrl.valueChanges
      .pipe(
        tap((val) => {
          const currentDisplay = this.display() ?? {};
          this.displayChange.emit({
            ...currentDisplay,
            math: { ...currentDisplay.math, defaultPrecision: val ?? undefined },
          });
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  // --- Inputs ---
  readonly params = input.required<GraphpadParams>();
  readonly display = input<GraphpadDisplayConfig>();
  readonly coordSystem = input<CoordinateSystemConfig | undefined>();
  readonly behaviorControls = input<WidgetControl[]>([]);
  readonly reactions = input<ReactionGroup[]>([]);

  // --- Outputs ---
  readonly paramsChange = output<GraphpadParams>();
  readonly displayChange = output<GraphpadDisplayConfig>();
  readonly coordSystemChange = output<CoordinateSystemConfig>();
  readonly behaviorControlsChange = output<WidgetControl[]>();
  readonly reactionsChange = output<ReactionGroup[]>();

  // --- Add bar: category selector ---
  readonly addCategoryCtrl = new FormControl<string>('element', { nonNullable: true });
  readonly addCategory = signal<string>('element');

  // --- Add element controls ---
  readonly newEntryTypeCtrl = new FormControl<string>('', { nonNullable: true });
  private readonly selectedAddType = signal('');

  // --- Add control controls ---
  readonly newControlTypeCtrl = new FormControl<string>('', { nonNullable: true });
  readonly newControlIdCtrl = new FormControl<string>('', { nonNullable: true });

  readonly defaultNewId = computed(() => {
    const raw = this.selectedAddType();
    if (!raw) {
      return '';
    }
    const [, type] = raw.split(':');
    const existingIds = Object.keys(this.params());
    return this.generateId(type, existingIds);
  });

  // --- Graphpad-specific ---
  readonly defaultPrecisionCtrl = new FormControl<number | null>(null);
  readonly newEntryIdCtrl = new FormControl<string>('', { nonNullable: true });

  // --- Add category options ---
  readonly addCategoryOptions: SelectOption[] = [
    { label: 'Element', value: 'element' },
    { label: 'Interaction', value: 'interaction' },
  ];

  // --- Add options (split by category) ---
  readonly elementAddOptions: SelectOption[] = [
    ...getFunctionTypes().map((t) => ({ label: `${t} · function`, value: `fn:${t}` })),
    ...getFeatureTypes().map((t) => ({ label: `${t} · feature`, value: `ft:${t}` })),
    ...getPrimitiveTypes().map((t) => ({ label: `${t} · primitive`, value: `pr:${t}` })),
  ];

  readonly interactionAddOptions: SelectOption[] = [
    { label: 'Slider', value: 'slider' },
    { label: 'Button', value: 'button' },
    { label: 'Data Table', value: 'data-table' },
    { label: 'Reaction', value: 'reaction' },
  ];

  // --- Computed signals ---

  readonly flatEntries = computed<FlatEntry[]>(() => {
    const params = this.params();
    const displayConfig = this.display();
    const controls = this.behaviorControls();

    const entries: FlatEntry[] = [];

    for (const [id, def] of Object.entries(params)) {
      const fn = isFunctionElementDef(def);
      const ft = isFeatureElementDef(def);
      const pr = isPrimitiveElementDef(def);

      entries.push({
        trackId: `el-${id}`,
        id,
        displayName: id,
        badge: def.type,
        category: fn ? 'function' : ft ? 'feature' : 'primitive',
        def,
        display: displayConfig?.elements?.[id],
        isFunction: fn,
        isFeature: ft,
        isPrimitive: pr,
      });
    }

    for (let i = 0; i < controls.length; i++) {
      const ctrl = controls[i];
      entries.push({
        trackId: `ctrl-${ctrl.id}`,
        id: ctrl.id,
        displayName: ctrl.label || ctrl.id,
        badge: ctrl.type,
        category: 'control',
        control: ctrl,
        controlIndex: i,
      });
    }

    const order: Record<EntryCategory, number> = { function: 0, feature: 1, primitive: 2, control: 3 };
    entries.sort((a, b) => order[a.category] - order[b.category]);

    return entries;
  });

  readonly elementEntries = computed(() => this.flatEntries().filter((e) => e.category !== 'control'));
  readonly controlEntries = computed(() => this.flatEntries().filter((e) => e.category === 'control'));

  readonly functionElementIds = computed<string[]>(() => {
    const params = this.params();
    return Object.entries(params)
      .filter(([, def]) => isFunctionElementDef(def))
      .map(([id]) => id);
  });

  readonly allElementIds = computed<string[]>(() => Object.keys(this.params()));

  // ═══════════════════════════════════════════════════════════════════════════
  // Canvas bridge
  // ═══════════════════════════════════════════════════════════════════════════

  onCanvasConfigChange(canvas: CanvasConfig): void {
    const currentDisplay = this.display() ?? {};
    this.displayChange.emit({ ...currentDisplay, canvas });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Control bridge
  // ═══════════════════════════════════════════════════════════════════════════

  onControlChange(idx: number, control: WidgetControl): void {
    const controls = [...this.behaviorControls()];
    controls[idx] = control;

    // Propagate variant to all sliders for consistency
    if (control.type === WIDGET_CONTROL_TYPE.Slider) {
      const variant = (control as WidgetSliderControl).variant;
      for (let i = 0; i < controls.length; i++) {
        if (i !== idx && controls[i].type === WIDGET_CONTROL_TYPE.Slider) {
          controls[i] = { ...controls[i], variant } as WidgetControl;
        }
      }
    }

    this.behaviorControlsChange.emit(controls);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Element handling
  // ═══════════════════════════════════════════════════════════════════════════

  onElementParamsChange(elementId: string, entry: FlatEntry, partialParams: Record<string, unknown>): void {
    const currentParams = this.params();
    const currentDef = currentParams[elementId];

    if (entry.isPrimitive) {
      const primDef = currentDef as PrimitiveElementDef;
      this.paramsChange.emit({
        ...currentParams,
        [elementId]: {
          ...primDef,
          params: { ...primDef.params, ...partialParams },
        },
      });
    } else if (entry.isFunction) {
      const fnDef = currentDef as FunctionElementDef;
      this.paramsChange.emit({
        ...currentParams,
        [elementId]: {
          ...fnDef,
          params: { ...(fnDef.params ?? {}), ...partialParams } as Record<string, ValueExpression>,
        },
      });
    } else {
      const ftDef = currentDef as FeatureElementDef;
      this.paramsChange.emit({
        ...currentParams,
        [elementId]: {
          ...ftDef,
          params: { ...(ftDef.params ?? {}), ...partialParams },
        },
      });
    }
  }

  onElementDisplayChange(elementId: string, newDisplay: ElementDisplayConfig): void {
    const currentDisplay = this.display() ?? {};
    this.displayChange.emit({
      ...currentDisplay,
      elements: {
        ...(currentDisplay.elements ?? {}),
        [elementId]: newDisplay,
      },
    });
  }

  onDeleteElement(elementId: string): void {
    const currentParams = { ...this.params() };
    delete currentParams[elementId];
    this.paramsChange.emit(currentParams);

    const currentDisplay = this.display() ?? {};
    if (currentDisplay.elements?.[elementId]) {
      const elements = { ...currentDisplay.elements };
      delete elements[elementId];
      this.displayChange.emit({ ...currentDisplay, elements });
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Add + Delete (unified)
  // ═══════════════════════════════════════════════════════════════════════════

  onDeleteEntry(entry: FlatEntry): void {
    if (entry.category === 'control') {
      const controls = [...this.behaviorControls()];
      controls.splice(entry.controlIndex!, 1);
      this.behaviorControlsChange.emit(controls);
    } else {
      this.onDeleteElement(entry.id);
    }
  }

  onAddElement(): void {
    const raw = this.newEntryTypeCtrl.value;
    if (!raw) {
      return;
    }

    const [category, type] = raw.split(':');
    const customId = this.newEntryIdCtrl.value.trim();
    const existingIds = Object.keys(this.params());
    const id = customId || this.generateId(type, existingIds);
    if (existingIds.includes(id)) {
      return;
    }

    if (category === 'fn') {
      const meta = getFunctionMeta(type);
      this.paramsChange.emit({
        ...this.params(),
        [id]: { type: type as FunctionType, params: meta ? { ...meta.defaultParams } : {} },
      });
    } else if (category === 'ft') {
      const firstFnId = this.functionElementIds()[0];
      const defaults = getFeatureDefaultParams(type);
      this.paramsChange.emit({
        ...this.params(),
        [id]: {
          type: type as FeatureType,
          params: { ...(firstFnId ? { of: firstFnId } : {}), ...defaults },
        },
      });
    } else if (category === 'pr') {
      const defaults = getPrimitiveDefaultParams(type as PrimitiveType);
      this.paramsChange.emit({
        ...this.params(),
        [id]: { type: type as PrimitiveType, params: defaults },
      });
    }

    this.newEntryTypeCtrl.setValue('', { emitEvent: false });
    this.newEntryIdCtrl.setValue('', { emitEvent: false });
  }

  onAddInteraction(): void {
    const type = this.newControlTypeCtrl.value;
    if (!type) {
      return;
    }

    if (type === 'reaction') {
      const groups = [...(this.reactions() ?? [])];
      groups.push({
        match: [{ when: '', dispatch: { action: 'PATCH_DISPLAY', payload: {} } }],
      });
      this.reactionsChange.emit(groups);
      this.newControlTypeCtrl.setValue('', { emitEvent: false });
      return;
    }

    const customId = this.newControlIdCtrl.value.trim();
    const controls = [...this.behaviorControls()];
    const id = customId || `ctrl-${Date.now()}`;

    if (type === 'slider') {
      controls.push({
        type: WIDGET_CONTROL_TYPE.Slider,
        id,
        label: 'New Slider',
        param: '',
        range: [-10, 10, 0.1],
      } as WidgetSliderControl);
    } else if (type === 'button') {
      controls.push({
        type: WIDGET_CONTROL_TYPE.Button,
        id,
        label: 'New Button',
        trigger: { action: 'PATCH_DISPLAY', payload: {} },
      } as WidgetButtonControl);
    } else if (type === 'data-table') {
      controls.push({
        type: WIDGET_CONTROL_TYPE.DataTable,
        id,
        label: 'New Table',
        columns: [{ key: 'col1', header: 'Column 1' }],
        rows: [{ values: { col1: '' } }],
        position: 'right',
      } as WidgetDataTableControl);
    }
    this.behaviorControlsChange.emit(controls);

    this.newControlTypeCtrl.setValue('', { emitEvent: false });
    this.newControlIdCtrl.setValue('', { emitEvent: false });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Private methods
  // ═══════════════════════════════════════════════════════════════════════════

  private syncPrecisionFromConfig(): void {
    const dp = this.display()?.math?.defaultPrecision;
    this.defaultPrecisionCtrl.setValue(dp ?? null, { emitEvent: false });
  }

  private generateId(type: string, existingIds: string[]): string {
    const baseId = type.charAt(0);
    let id = baseId;
    let counter = 2;
    while (existingIds.includes(id)) {
      id = `${baseId}${counter}`;
      counter++;
    }
    return id;
  }
}
